import React, { ReactNode, useCallback } from "react";
import { DropzoneOptions, ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import cn from "classnames";
import { toast } from "react-toastify";
import { useSharedTranslations } from "../../lib";
import styles from "./file-drag-n-drop.module.scss";
import { CircularProgress } from "../circular-progress/circular-progress";


interface IFileDragNDropProps extends DropzoneOptions {
    containerClassName?: string;
    loading?: boolean;
    loadingText?: string;
    dropzoneClassName?: string;
    textClass?: string;
    subTextClass?: string;
    isIconTextAligned?: boolean;
    onAddFiles?: (files: File[]) => void;
    icon?: ReactNode;
    text?: string;
    subtext?: string;
    dropText?: string;
    accept?: DropzoneOptions["accept"];
}

export const FileDragNDrop: React.FC<IFileDragNDropProps> = ({
                                                                 onAddFiles,
                                                                 loading = false,
                                                                 loadingText,
                                                                 icon,
                                                                 text,
                                                                 subtext,
                                                                 dropText,
                                                                 containerClassName,
                                                                 dropzoneClassName,
                                                                 textClass,
                                                                 subTextClass,
                                                                 isIconTextAligned = false,
                                                                 ...props
                                                             }) => {
    const t = useSharedTranslations();
    const dropOptions: DropzoneOptions = {
        // 10 mb
        maxSize: 10485760,
        onDrop: useCallback((acceptedFiles: any, fileRejections: FileRejection[]) => {
            if (onAddFiles) {
                onAddFiles(acceptedFiles);
            }

            fileRejections.forEach((file) => {
                file.errors.forEach((err) => {
                    if (err.code === ErrorCode.FileTooLarge) {
                        toast.error(t("files.fileIsTooLarge"));
                    }
                });
            });

        }, []),
        ...props
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone(dropOptions);

    return (
        <div {...getRootProps()} className={containerClassName}>
            <input {...getInputProps() as any} />
            {!loading &&
                (isDragActive ?
                    <div className={cn(styles.droppableContainer, dropzoneClassName)}>
                        <div className={cn(styles.fileLine, styles.initialFileLine, textClass)}>
                            {dropText ? dropText : t("files.drop")}
                        </div>
                    </div> :
                    <div className={cn(styles.droppableContainer, dropzoneClassName)}>
                        {!isIconTextAligned && icon}
                        {!isIconTextAligned && <div
                            className={cn(styles.fileLine, styles.initialFileLine, textClass)}>{text ? text : t("files.drag")}</div>}
                        {isIconTextAligned && <div className={styles.flexIcon}>
                            {icon}
                            <div
                                className={cn(styles.fileLine, styles.initialFileLine, textClass)}>{text ? text : t("files.drag")}</div>
                        </div>}
                        <div className={cn(styles.fileLine, subTextClass)}>{subtext ? subtext : t("files.choose")}</div>
                    </div>)
            }
            {loading &&
                <div className={cn(styles.droppableContainer, styles.loadingContainer, dropzoneClassName)}>
                    <CircularProgress size={60} className={styles.loader}/>
                    {loadingText && <div className={styles.loaderText}>{loadingText}</div>}
                </div>}
        </div>
    );
};
